import type { Ring } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { boxSolid, octahedronSolid, subFrame } from '../solids';
import { corners } from './genericBlock';

/** Working furniture is a reconstruction, kept off the court's approach and shared with collision. */
export const PIETERS_COURT = {
  bread: { u0: -10.2, u1: -7.4, v0: 7.0, v1: 8.2 },
  linen: { u0: 7.4, u1: 10.2, v0: 0.0, v1: 1.2 },
  bench: { u0: -11.7, u1: -8.5, v0: -3.6, v1: -3.0 },
  drying: { u0: 3.2, u1: 11.8, v0: -5.0, v1: -4.1 },
} as const;

export function pietersCourtObstacles(): Ring[] {
  return Object.values(PIETERS_COURT).map(corners);
}

export function buildGasthuisCourt(
  builder: MeshBuilder,
  frame: Frame,
  timber: VertexStyle,
  cloth: VertexStyle,
  bread: VertexStyle
): void {
  const solid = (
    style: VertexStyle,
    u0: number,
    u1: number,
    v0: number,
    v1: number,
    y0: number,
    y1: number
  ) => {
    boxSolid(builder, style, frame, [u0, y0, -v1], [u1, y1, -v0], { top: true, bottom: false });
  };
  for (const table of [PIETERS_COURT.bread, PIETERS_COURT.linen]) {
    solid(timber, table.u0, table.u1, table.v0, table.v1, 0.75, 0.86);
    for (const u of [table.u0 + 0.12, table.u1 - 0.24])
      for (const v of [table.v0 + 0.12, table.v1 - 0.24])
        solid(timber, u, u + 0.12, v, v + 0.12, 0, 0.75);
    solid(timber, table.u0 + 0.18, table.u1 - 0.18, table.v0 + 0.55, table.v0 + 0.65, 0.2, 0.28);
  }
  for (let loaf = 0; loaf < 6; loaf++) {
    const u = PIETERS_COURT.bread.u0 + 0.45 + (loaf % 3) * 0.9;
    const v = PIETERS_COURT.bread.v0 + 0.3 + Math.floor(loaf / 3) * 0.55;
    const f = subFrame(frame, u, v, 0, 0.98);
    octahedronSolid(builder, bread, f, [0, 0, 0], 0.2);
  }
  for (let pile = 0; pile < 3; pile++)
    for (let fold = 0; fold < 3; fold++) {
      const u = PIETERS_COURT.linen.u0 + 0.2 + pile * 0.85 + (fold % 2) * 0.035;
      solid(cloth, u, u + 0.62, 0.2, 1.0, 0.86 + fold * 0.065, 0.92 + fold * 0.065);
    }
  const bench = PIETERS_COURT.bench;
  solid(timber, bench.u0, bench.u1, bench.v0, bench.v1, 0.42, 0.51);
  for (const u of [bench.u0 + 0.18, bench.u1 - 0.3])
    solid(timber, u, u + 0.12, bench.v0 + 0.12, bench.v1 - 0.12, 0, 0.42);
  const line = PIETERS_COURT.drying;
  for (const u of [line.u0, line.u1 - 0.12]) solid(timber, u, u + 0.12, -4.65, -4.53, 0, 2.45);
  solid({ ...timber, surface: SURFACE.cloth }, line.u0, line.u1, -4.61, -4.58, 2.32, 2.35);
  for (let sheet = 0; sheet < 3; sheet++) {
    const u = line.u0 + 0.5 + sheet * 2.6;
    solid(cloth, u, u + 1.7, -4.64, -4.56, 1.35 + sheet * 0.08, 2.32);
  }
}
